/**
 * TalentLens AI - Analytics Page
 * Enhanced with recharts visualizations and dark mode support
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import api from '../lib/api';

const COLORS = ['#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626', '#7C2D12', '#4338CA', '#0891B2'];

export default function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState(30);

  const { data: skillsData, isLoading: skillsLoading, error: skillsError } = useQuery({
    queryKey: ['analytics-skills', timeRange],
    queryFn: () => api.getSkillAnalytics(timeRange),
  });

  const { data: pipelineData, isLoading: pipelineLoading, error: pipelineError } = useQuery({
    queryKey: ['analytics-pipeline', timeRange],
    queryFn: () => api.getPipelineAnalytics(timeRange),
  });

  const { data: matchingData, isLoading: matchingLoading, error: matchingError } = useQuery({
    queryKey: ['analytics-matching', timeRange],
    queryFn: () => api.getMatchingAnalytics(timeRange),
  });

  const { data: trendData, error: trendError } = useQuery({
    queryKey: ['analytics-trends', timeRange],
    queryFn: () => api.getAnalyticsTrends(timeRange),
  });

  const isLoading = skillsLoading || pipelineLoading || matchingLoading;
  const hasErrors = skillsError || pipelineError || matchingError || trendError;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 h-80" />
          ))}
        </div>
      </div>
    );
  }

  const skills = skillsData?.data?.skills || [];
  const processingTimes = pipelineData?.data?.processingTimes || [];
  const topErrors = pipelineData?.data?.topErrors || [];
  const topMatchedJobs = matchingData?.data?.topMatchedJobs || [];
  const dailyStats = trendData?.data?.dailyStats || [];
  const statusBreakdown = pipelineData?.data?.statusBreakdown || [];

  // Prepare chart data
  const skillsChartData = skills.slice(0, 10).map((s: any) => ({
    name: s.skill.length > 12 ? s.skill.slice(0, 12) + '...' : s.skill,
    fullName: s.skill,
    count: s.count,
    avgYears: s.avgYears || 0,
  }));

  const processingChartData = processingTimes.map((pt: any) => ({
    name: pt.range,
    value: pt.count,
  }));

  const matchedJobsChartData = topMatchedJobs.slice(0, 8).map((job: any) => ({
    name: job.title.length > 15 ? job.title.slice(0, 15) + '...' : job.title,
    fullName: job.title,
    matches: job.matchCount,
  }));

  const categoriesData = skillsData?.data?.categories
    ? Object.entries(skillsData.data.categories)
        .filter(([, skills]: [string, any]) => skills.length > 0)
        .map(([category, categorySkills]: [string, any]) => ({
          name: category.charAt(0).toUpperCase() + category.slice(1),
          value: categorySkills.length,
        }))
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">Insights and metrics from your talent pipeline</p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Time range:</span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Error Display */}
      {hasErrors && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <WarningIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-900 dark:text-yellow-200">Some data unavailable</h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Some analytics data couldn't be loaded. Charts may be incomplete.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Matches"
          value={matchingData?.data?.totalMatches || 0}
          icon={<MatchIcon className="w-6 h-6" />}
          color="primary"
        />
        <StatCard
          title="CVs Processed"
          value={statusBreakdown.find((s: any) => s.status === 'processed')?.count || 0}
          icon={<DocumentIcon className="w-6 h-6" />}
          color="green"
        />
        <StatCard
          title="Active Jobs"
          value={topMatchedJobs.length}
          icon={<BriefcaseIcon className="w-6 h-6" />}
          color="blue"
        />
        <StatCard
          title="Success Rate"
          value={calculateSuccessRate(statusBreakdown)}
          icon={<CheckCircleIcon className="w-6 h-6" />}
          color="emerald"
        />
      </div>

      {/* Trends Chart */}
      {dailyStats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activity Trends</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyStats}>
                <defs>
                  <linearGradient id="colorUploads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorMatches" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} className="fill-gray-500 dark:fill-gray-400" />
                <YAxis tick={{ fontSize: 12 }} className="fill-gray-500 dark:fill-gray-400" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--tooltip-bg, white)',
                    border: '1px solid var(--tooltip-border, #E5E7EB)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  labelClassName="font-medium"
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="uploads"
                  name="CV Uploads"
                  stroke="#7C3AED"
                  fillOpacity={1}
                  fill="url(#colorUploads)"
                />
                <Area
                  type="monotone"
                  dataKey="matches"
                  name="Matches"
                  stroke="#2563EB"
                  fillOpacity={1}
                  fill="url(#colorMatches)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Skills Bar Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Skills in Talent Pool</h2>
          {skillsChartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={skillsChartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis type="number" tick={{ fontSize: 12 }} className="fill-gray-500 dark:fill-gray-400" />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={90}
                    className="fill-gray-500 dark:fill-gray-400"
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
                            <p className="font-medium text-gray-900 dark:text-white">{data.fullName}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{data.count} CVs</p>
                            <p className="text-sm text-gray-500 dark:text-gray-500">~{data.avgYears}y avg experience</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyChartState message="No skill data available" />
          )}
        </div>

        {/* Skills by Category Pie Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skills by Category</h2>
          {categoriesData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoriesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {categoriesData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, white)',
                      border: '1px solid var(--tooltip-border, #E5E7EB)',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`${value} skills`, 'Count']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyChartState message="No category data available" />
          )}
        </div>

        {/* Processing Times Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Processing Time Distribution</h2>
          {processingChartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={processingChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} className="fill-gray-500 dark:fill-gray-400" />
                  <YAxis tick={{ fontSize: 12 }} className="fill-gray-500 dark:fill-gray-400" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, white)',
                      border: '1px solid var(--tooltip-border, #E5E7EB)',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`${value} CVs`, 'Count']}
                  />
                  <Bar dataKey="value" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyChartState message="No processing data available" />
          )}
        </div>

        {/* Top Matched Jobs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Most Matched Jobs</h2>
          {matchedJobsChartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={matchedJobsChartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis type="number" tick={{ fontSize: 12 }} className="fill-gray-500 dark:fill-gray-400" />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={120}
                    className="fill-gray-500 dark:fill-gray-400"
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
                            <p className="font-medium text-gray-900 dark:text-white">{data.fullName}</p>
                            <p className="text-sm text-primary-600 dark:text-primary-400">{data.matches} matches</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="matches" fill="#2563EB" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyChartState message="No matching data available" />
          )}
        </div>
      </div>

      {/* CV Status Breakdown */}
      {statusBreakdown.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">CV Processing Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {statusBreakdown.map((status: any) => (
              <div
                key={status.status}
                className={`p-4 rounded-lg ${getStatusStyle(status.status)}`}
              >
                <p className="text-2xl font-bold">{status.count}</p>
                <p className="text-sm capitalize">{status.status}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Common Processing Issues */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Common Processing Issues</h2>
        {topErrors.length > 0 ? (
          <div className="space-y-3">
            {topErrors.slice(0, 5).map((error: any, i: number) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-100 dark:border-red-800"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                    <span className="text-red-600 dark:text-red-400 font-medium">{i + 1}</span>
                  </div>
                  <span className="text-sm text-red-800 dark:text-red-300">{error.error}</span>
                </div>
                <span className="text-sm text-red-600 dark:text-red-400 font-medium px-3 py-1 bg-red-100 dark:bg-red-900/50 rounded-full">
                  {error.count} occurrences
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-medium">No processing errors</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">All CVs are being processed successfully</p>
          </div>
        )}
      </div>
    </div>
  );
}

function calculateSuccessRate(statusBreakdown: any[]): string {
  if (!statusBreakdown || statusBreakdown.length === 0) return '100%';
  const processed = statusBreakdown.find((s: any) => s.status === 'processed')?.count || 0;
  const failed = statusBreakdown.find((s: any) => s.status === 'failed')?.count || 0;
  const total = processed + failed;
  if (total === 0) return '100%';
  return `${Math.round((processed / total) * 100)}%`;
}

function getStatusStyle(status: string): string {
  const styles: Record<string, string> = {
    processed: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
    pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
    processing: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    failed: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    partial: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
  };
  return styles[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
}

function EmptyChartState({ message }: { message: string }) {
  return (
    <div className="h-80 flex items-center justify-center">
      <div className="text-center">
        <ChartIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-gray-500 dark:text-gray-400">{message}</p>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'primary' | 'green' | 'blue' | 'emerald';
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  const colorStyles = {
    primary: 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400',
    green: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    blue: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
        <div className={`p-2 rounded-lg ${colorStyles[color]}`}>{icon}</div>
      </div>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

// Icons
function MatchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}
